// src/pages/Dashboard/student/PhysicsLab.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import PHYSICS_EXPERIMENTS from "../../../data/physicsExperiments";
import MagneticExperiment from "../physics/experiments/class6/MagneticExperiment.jsx";
import ReflectionMirror from "../physics/experiments/class7/ReflectionMirror.jsx";
import SoundVibration from "../physics/experiments/class8/SoundVibration.jsx";
import OhmsLawExperiment from "../physics/experiments/class9/OhmsLawExperiment.jsx";
import RefractionGlassSlab from "../physics/experiments/class10/RefractionGlassSlab.jsx";
import { useTranslation } from "react-i18next";

export default function PhysicsLab() {
  const { t } = useTranslation();

  const { experimentId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const selectedClassParam = searchParams.get("class") || "";
  const [selectedClass, setSelectedClass] = useState(selectedClassParam);

  const [progress, setProgress] = useState({});

  const experiments = selectedClass
    ? PHYSICS_EXPERIMENTS[selectedClass] || []
    : [];

  useEffect(() => {
    const saved = JSON.parse(
      localStorage.getItem("physicsLabProgress") || "{}"
    );
    setProgress(saved);
  }, []);

  const handleCompletion = (experimentId) => {
    const updated = { ...progress, [experimentId]: "completed" };
    setProgress(updated);
    localStorage.setItem("physicsLabProgress", JSON.stringify(updated));
  };

  if (experimentId) {
    const allExperiments = Object.values(PHYSICS_EXPERIMENTS).flat();
    const experiment = allExperiments.find((exp) => exp.id === experimentId);

    if (!experiment)
      return <div className="p-6 text-red-500">{t("physicsLab.notFound")}</div>;

    return (
      <div className="p-6">
        <button
          onClick={() =>
            navigate(`/dashboard/student/physics?class=${selectedClass}`)
          }
          className="mb-4 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
        >
          {t("physicsLab.back")}
        </button>

        <h1 className="text-3xl font-bold text-blue-800">{experiment.title}</h1>
        <p className="text-gray-600 mt-2 mb-8">{experiment.description}</p>

        {/* Render experiment component by ID */}
        {experiment.id === "magnet_attraction" && (
          <MagneticExperiment
            onComplete={() => handleCompletion("magnet_attraction")}
          />
        )}

        {experiment.id === "reflection_mirror" && (
          <ReflectionMirror
            onComplete={() => handleCompletion("reflection_mirror")}
          />
        )}

        {experiment.id === "sound_vibration" && (
          <SoundVibration
            onComplete={() => handleCompletion("sound_vibration")}
          />
        )}

        {experiment.id === "ohms_law" && (
          <OhmsLawExperiment onComplete={() => handleCompletion("ohms_law")} />
        )}

        {experiment.id === "refraction_glass_slab" && (
          <RefractionGlassSlab
            onComplete={() => handleCompletion("refraction_glass_slab")}
          />
        )}
      </div>
    );
  }

  //        ────────────────────  EXPERIMENT LIST

  return (
    <div className="min-h-[calc(100vh-120px)] bg-gradient-to-br from-yellow-50 to-orange-100 p-6 flex flex-col items-center w-full">
      {/* BACK BUTTON */}
      <button
        onClick={() => navigate("/dashboard/student/virtual-lab")}
        className="self-start mb-4 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
      >
        {t("physicsLab.back")}
      </button>

      {/* TITLE */}
      <h1 className="text-3xl font-bold mb-6 text-orange-700">
        {t("physicsLab.title")}
      </h1>

      {/* CLASS SELECTOR */}
      <div className="mb-8 flex gap-4 items-center">
        <label className="text-gray-700 font-medium text-lg">
          {t("physicsLab.selectClassLabel")}
        </label>

        <select
          value={selectedClass}
          onChange={(e) => {
            setSelectedClass(e.target.value);
            navigate(`/dashboard/student/physics?class=${e.target.value}`);
          }}
          className="
          px-4 py-2 rounded-xl 
          border border-orange-300 
          text-gray-700 bg-white
          shadow-sm
          focus:ring-2 focus:ring-orange-400 focus:border-orange-400 
          transition-all
        "
        >
          <option value="">{t("physicsLab.chooseClass")}</option>
          <option value="6">{t("virtualLab.class")} 6</option>
          <option value="7">{t("virtualLab.class")} 7</option>
          <option value="8">{t("virtualLab.class")} 8</option>
          <option value="9">{t("virtualLab.class")} 9</option>
          <option value="10">{t("virtualLab.class")} 10</option>
        </select>
      </div>

      {/* EXPERIMENT CARDS */}
      {!selectedClass ? (
        <p className="text-gray-600 italic">{t("physicsLab.noClass")}</p>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {experiments.map((exp) => (
            <div
              key={exp.id}
              className="
              bg-white p-6 rounded-xl 
              shadow-md border border-orange-200
              transition-all 
              hover:scale-[1.04] 
              hover:shadow-[0_0_16px_#f59f0b55]
            "
            >
              <h3 className="text-lg font-semibold text-orange-700">
                {exp.title}
              </h3>

              <p className="text-gray-600 text-sm mt-2">{exp.description}</p>

              <button
                onClick={() =>
                  navigate(
                    `/dashboard/student/physics/${exp.id}?class=${selectedClass}`
                  )
                }
                className="
                mt-4 bg-orange-500 text-white 
                px-3 py-1 rounded text-sm 
                hover:bg-orange-600 transition
              "
              >
               {t("physicsLab.launch")}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
